import React, { useEffect, useRef } from 'react';

interface Star {
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
  twinkleSpeed: number;
  twinklePhase: number;
}

interface TechElement {
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
  type: 'circuit' | 'binary' | 'node';
  rotation: number;
  rotationSpeed: number;
}

export const StarryBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const starsRef = useRef<Star[]>([]);
  const techElementsRef = useRef<TechElement[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createStars = () => {
      const stars: Star[] = [];
      const numStars = Math.floor((canvas.width * canvas.height) / 8000);

      for (let i = 0; i < numStars; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 0.5,
          speed: Math.random() * 0.5 + 0.1,
          opacity: Math.random() * 0.8 + 0.2,
          twinkleSpeed: Math.random() * 0.02 + 0.01,
          twinklePhase: Math.random() * Math.PI * 2
        });
      }
      starsRef.current = stars;
    };

    const createTechElements = () => {
      const elements: TechElement[] = [];
      const numElements = 15;

      for (let i = 0; i < numElements; i++) {
        elements.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 30 + 20,
          speed: Math.random() * 0.3 + 0.1,
          opacity: Math.random() * 0.3 + 0.1,
          type: ['circuit', 'binary', 'node'][Math.floor(Math.random() * 3)] as 'circuit' | 'binary' | 'node',
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.01
        });
      }
      techElementsRef.current = elements;
    };

    const drawStar = (star: Star, time: number) => {
      const twinkle = Math.sin(time * star.twinkleSpeed + star.twinklePhase) * 0.3 + 0.7;
      const alpha = star.opacity * twinkle;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = star.size * 2;
      ctx.shadowColor = '#ffffff';

      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      ctx.fill();

      // Add cross sparkle effect for larger stars
      if (star.size > 1.5) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(star.x - star.size * 2, star.y);
        ctx.lineTo(star.x + star.size * 2, star.y);
        ctx.moveTo(star.x, star.y - star.size * 2);
        ctx.lineTo(star.x, star.y + star.size * 2);
        ctx.stroke();
      }

      ctx.restore();
    };

    const drawTechElement = (element: TechElement, _time: number) => {
      ctx.save();
      ctx.globalAlpha = element.opacity;
      ctx.translate(element.x, element.y);
      ctx.rotate(element.rotation);

      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, element.size);
      gradient.addColorStop(0, 'rgba(0, 255, 255, 0.6)');
      gradient.addColorStop(0.5, 'rgba(0, 150, 255, 0.3)');
      gradient.addColorStop(1, 'rgba(0, 100, 200, 0.1)');

      switch (element.type) {
        case 'circuit':
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 1;
          ctx.beginPath();
          // Draw circuit pattern
          for (let i = 0; i < 4; i++) {
            const angle = (i * Math.PI) / 2;
            const x1 = Math.cos(angle) * element.size * 0.3;
            const y1 = Math.sin(angle) * element.size * 0.3;
            const x2 = Math.cos(angle) * element.size * 0.7;
            const y2 = Math.sin(angle) * element.size * 0.7;
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
          }
          ctx.stroke();
          break;

        case 'binary':
          ctx.fillStyle = gradient;
          ctx.font = `${element.size * 0.3}px monospace`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const binaryText = Math.random() > 0.5 ? '1' : '0';
          ctx.fillText(binaryText, 0, 0);
          break;

        case 'node':
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(0, 0, element.size * 0.2, 0, Math.PI * 2);
          ctx.fill();

          // Draw connecting lines
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 0.5;
          for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3;
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(Math.cos(angle) * element.size * 0.5, Math.sin(angle) * element.size * 0.5);
            ctx.stroke();
          }
          break;
      }

      ctx.restore();
    };

    const animate = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Create gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#0a0a1a');
      gradient.addColorStop(0.5, '#1a1a2e');
      gradient.addColorStop(1, '#16213e');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw stars
      starsRef.current.forEach((star) => {
        star.y += star.speed;
        if (star.y > canvas.height + 10) {
          star.y = -10;
          star.x = Math.random() * canvas.width;
        }
        drawStar(star, time);
      });

      // Update and draw tech elements
      techElementsRef.current.forEach((element) => {
        element.y += element.speed;
        element.rotation += element.rotationSpeed;
        if (element.y > canvas.height + element.size) {
          element.y = -element.size;
          element.x = Math.random() * canvas.width;
        }
        drawTechElement(element, time);
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    resizeCanvas();
    createStars();
    createTechElements();
    animate(0);

    const handleResize = () => {
      resizeCanvas();
      createStars();
      createTechElements();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className='fixed inset-0 -z-10 h-full w-full'
      style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 50%, #16213e 100%)' }}
    />
  );
};
